<template>
  <div class="rel-dash">
    <div class="rel-dash__actions bloco">
      <p class="fonte-fraca rel-dash__meta">
        Semana <strong>{{ payload?.semana ?? "—" }}</strong>
        · {{ payload?.period?.startDt }} até {{ payload?.period?.endDt }}
      </p>
      <button type="button" class="acao-secundaria" :disabled="!payload || exporting" @click="baixarPdf">
        {{ exporting ? "Gerando PDF…" : "Baixar PDF" }}
      </button>
    </div>

    <div v-if="!payload" class="bloco rel-dash__vazio alinha-centro fonte-fraca">Nenhum dado para o período.</div>

    <div v-else id="relatorio-usinagens-pdf" ref="pdfRoot" class="rel-dash__conteudo">
      <header class="rel-dash__capa">
        <h2>RELATÓRIO DE USINAGENS</h2>
        <p>SEMANA {{ payload.semana }}</p>
      </header>

      <p v-if="!temApontamentosReais" class="rel-dash__info-geral fonte-menor">
        Ainda não há apontamentos de produção vinculados às Routers e à CNC no período.
        O relatório exibe apenas as paradas programadas (ginástica e almoço) até que os operadores
        registrem apontamentos com <strong>maquina_id</strong> ou indiquem a máquina na observação.
      </p>

      <section
        v-for="maq in payload.maquinas"
        :key="maq.nome"
        class="bloco rel-dash__maq-section"
        :class="{ 'rel-dash__maq-section--aviso': !maq.cadastrado }"
      >
        <h3 class="rel-dash__sec-titulo rel-dash__sec-titulo--maq">{{ maq.nome }}</h3>

        <p v-if="!maq.cadastrado" class="rel-dash__aviso fonte-menor">
          Máquina «{{ maq.nome }}» ainda não cadastrada em inj_maquina. Cadastre-a para vincular apontamentos.
        </p>
        <p v-else class="fonte-fraca fonte-menor rel-dash__setor-nome">
          Máquina: {{ maq.nomeMaquinaCadastro || maq.nome }}
          <span v-if="(maq.minutosApontados ?? 0) > 0">
            · Apontado: {{ formatarMinutos(maq.minutosApontados) }}
          </span>
        </p>

        <div class="rel-dash__table-wrap rel-dash__table-wrap--eventos">
          <table class="tabela rel-dash__tabela rel-dash__tabela--eventos">
            <thead>
              <tr>
                <th>DATA</th>
                <th>SEMANA</th>
                <th>OPERADOR</th>
                <th>OP</th>
                <th>CÓDIGO</th>
                <th>EVENTO</th>
                <th>HORA INICIAL</th>
                <th>HORA FINAL</th>
                <th>FINALIZAÇÃO DE EVENTO</th>
                <th>OBSERVAÇÃO</th>
                <th>DURAÇÃO</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!(maq.eventos && maq.eventos.length)">
                <td colspan="11" class="alinha-centro fonte-fraca">Nenhum evento no período.</td>
              </tr>
              <tr v-for="(ev, idx) in maq.eventos || []" :key="maq.nome + '-ev-' + idx">
                <td>{{ ev.dataFormatada || ev.data }}</td>
                <td class="alinha-centro">{{ ev.semana }}</td>
                <td>{{ ev.operador || "SEM OPERADOR" }}</td>
                <td class="alinha-centro">{{ ev.op ?? "-" }}</td>
                <td>{{ ev.codigo || "-" }}</td>
                <td>
                  <span class="rel-dash__evento-chip" :class="'rel-dash__evento-chip--' + (ev.eventoChave || 'outro')">
                    {{ ev.evento }}
                  </span>
                </td>
                <td>{{ ev.horaInicial }}</td>
                <td>{{ ev.horaFinal }}</td>
                <td>{{ ev.finalizacaoEvento }}</td>
                <td class="rel-dash__obs">{{ ev.observacao || "" }}</td>
                <td><strong>{{ ev.duracao }}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="rel-dash__resumo">
          <div class="rel-dash__usinagem-body">
            <div class="rel-dash__plot rel-dash__plot--pie">
              <Pie v-if="maq.fatias?.length" :data="chartData(maq)" :options="pieOptions" />
              <p v-else class="alinha-centro fonte-fraca">Sem dados para gráfico</p>
            </div>

            <div class="rel-dash__legenda-wrap">
              <p class="rel-dash__tempo-disponivel fonte-fraca fonte-menor">
                Tempo disponível: <strong>{{ maq.tempoDisponivelFormatado }}</strong>
                <span v-if="maq.minutosApontados > 0"> · Apontado: {{ formatarMinutos(maq.minutosApontados) }}</span>
              </p>
              <ul class="rel-dash__legenda">
                <li v-for="fatia in maq.fatias" :key="maq.nome + '-' + fatia.chave">
                  <span class="rel-dash__bolha" :style="{ background: corFatia(fatia.chave) }"></span>
                  <div class="rel-dash__legenda-texto">
                    <span class="rel-dash__legenda-pct">{{ fatia.pct }}%</span>
                    <span>{{ textoFatia(fatia) }}</span>
                    <span class="rel-dash__legenda-tempo fonte-fraca">({{ fatia.tempoFormatado }})</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>

    <p class="fonte-fraca fonte-menor rel-dash__nota">
      A tabela inclui paradas programadas (ginástica/almoço) e, quando existirem, os apontamentos de produção
      vinculados à máquina (maquina_id ou observação). O gráfico resume o uso do tempo disponível.
    </p>
  </div>
</template>

<script>
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "vue-chartjs";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { exportElementToPdf } from "@/utils/kpiPdfExport";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const CORES = {
  parada: "#c94c4c",
  setup: "#b8860b",
  producao: "#3a7bd5",
  pd: "#5c5cad",
  ferramentaria: "#2e8b57",
};

export default {
  name: "RelatorioUsinagensDashboard",
  components: { Pie },
  props: {
    payload: { type: Object, default: null },
  },
  computed: {
    temApontamentosReais() {
      if (this.payload?.temApontamentosReais != null) {
        return Boolean(this.payload.temApontamentosReais);
      }
      return (this.payload?.maquinas ?? []).some((m) => (m.minutosApontados ?? 0) > 0);
    },
  },
  data() {
    return {
      exporting: false,
      pieOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: {
            color: "#fff",
            font: { weight: "bold", size: 13 },
            formatter: (v) => (v == null || Number(v) < 1 ? "" : `${Math.round(v)}%`),
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const fatia = ctx.chart.data.fatias?.[ctx.dataIndex];
                return `${fatia?.label ?? ctx.label}: ${ctx.raw}% (${fatia?.tempoFormatado ?? ""})`;
              },
            },
          },
        },
      },
    };
  },
  methods: {
    corFatia(chave) {
      return CORES[chave] || "#888";
    },
    textoFatia(fatia) {
      return fatia.label || fatia.descricao || fatia.chave;
    },
    formatarMinutos(min) {
      const h = Math.floor(min / 60);
      const m = min % 60;
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
    },
    chartData(maq) {
      const fatias = maq.fatias || [];
      return {
        labels: fatias.map((f) => `${f.pct}%`),
        fatias,
        datasets: [
          {
            data: fatias.map((f) => f.pct),
            backgroundColor: fatias.map((f) => this.corFatia(f.chave)),
          },
        ],
      };
    },
    async baixarPdf() {
      if (!this.payload || this.exporting) return;
      this.exporting = true;
      try {
        await this.$nextTick();
        await exportElementToPdf(this.$refs.pdfRoot, `relatorio-usinagens-semana-${this.payload.semana ?? "periodo"}.pdf`);
      } catch (e) {
        alert(e?.message || "Não foi possível gerar o PDF.");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.rel-dash {
  display: flex;
  flex-direction: column;
  gap: var(--margem);
  min-width: 0;
}

.rel-dash__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px var(--margem);
}

.rel-dash__meta {
  margin: 0;
}

.rel-dash__conteudo {
  display: flex;
  flex-direction: column;
  gap: var(--margem);
}

.rel-dash__capa {
  text-align: center;
  padding: 16px var(--margem);
  border-bottom: 2px solid var(--cor-primaria);
}

.rel-dash__capa h2 {
  margin: 0 0 4px;
  letter-spacing: 0.05em;
  font-size: 1.15rem;
}

.rel-dash__capa p {
  margin: 0;
  color: var(--cor-fonte-fraca);
}

.rel-dash__maq-section {
  padding: var(--margem);
  min-width: 0;
}

.rel-dash__maq-section--aviso {
  box-shadow: 0 0 0 1px #c48a00 inset;
}

.rel-dash__info-geral {
  margin: 0;
  padding: 10px 12px;
  border-radius: 6px;
  color: #3d4a5c;
  background: #eef3f9;
  border: 1px solid #c5d4e8;
  line-height: 1.45;
}

.rel-dash__aviso {
  margin: -6px 0 12px;
  padding: 8px 10px;
  border-radius: 6px;
  color: #5c4500;
  background: #fff6d9;
  border: 1px solid #e0c56a;
  line-height: 1.4;
}

.rel-dash__setor-nome {
  margin: -6px 0 14px;
}

.rel-dash__sec-titulo {
  margin: 0 0 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--cor-separador);
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: center;
}

.rel-dash__sec-titulo--maq {
  color: var(--cor-primaria-forte, var(--cor-primaria));
}

.rel-dash__table-wrap {
  overflow-x: auto;
  margin: 0 0 16px;
}

.rel-dash__table-wrap--eventos {
  border-radius: 8px;
  box-shadow: 0 0 0 1px var(--cor-separador) inset;
}

.rel-dash__tabela {
  width: 100%;
  font-size: 0.78rem;
  border-collapse: collapse;
}

.rel-dash__tabela th,
.rel-dash__tabela td {
  padding: 7px 8px;
  vertical-align: middle;
  white-space: nowrap;
  border-bottom: 1px dotted var(--cor-separador, #ccc);
}

.rel-dash__tabela th {
  background: var(--cor-cinza, #f0f0f0);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  position: sticky;
  top: 0;
  z-index: 1;
}

.rel-dash__tabela tbody tr:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

.rel-dash__tabela--eventos .rel-dash__obs {
  white-space: normal;
  min-width: 120px;
  max-width: 220px;
}

.rel-dash__evento-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.rel-dash__evento-chip--parada {
  background: rgba(201, 76, 76, 0.15);
  color: #a03333;
}

.rel-dash__evento-chip--setup {
  background: rgba(184, 134, 11, 0.15);
  color: #8b6508;
}

.rel-dash__evento-chip--producao {
  background: rgba(58, 123, 213, 0.15);
  color: #2a5a9e;
}

.rel-dash__evento-chip--pd {
  background: rgba(92, 92, 173, 0.15);
  color: #45458a;
}

.rel-dash__evento-chip--ferramentaria {
  background: rgba(46, 139, 87, 0.15);
  color: #1f6b45;
}

.rel-dash__resumo {
  margin-top: 4px;
}

.rel-dash__usinagem-body {
  display: grid;
  grid-template-columns: minmax(180px, 240px) 1fr;
  gap: 16px;
  align-items: start;
}

.rel-dash__plot {
  position: relative;
}

.rel-dash__plot--pie {
  height: 220px;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rel-dash__legenda-wrap {
  min-width: 0;
}

.rel-dash__tempo-disponivel {
  margin: 0 0 12px;
}

.rel-dash__legenda {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rel-dash__legenda li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.rel-dash__bolha {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
}

.rel-dash__legenda-texto {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.82rem;
  line-height: 1.35;
}

.rel-dash__legenda-pct {
  font-weight: 700;
  color: var(--cor-fonte-forte);
}

.rel-dash__legenda-tempo {
  font-size: 0.78rem;
}

.rel-dash__vazio {
  padding: 2rem;
}

.rel-dash__nota {
  line-height: 1.45;
  margin: 0;
}

@media screen and (max-width: 900px) {
  .rel-dash__usinagem-body {
    grid-template-columns: 1fr;
  }

  .rel-dash__plot--pie {
    height: 260px;
    max-width: 280px;
    margin: 0 auto;
  }
}
</style>
